/**
 * 鉴权 store（架构 §2 src/store/auth.store.ts / T09）
 *  - token / user 持久化到 localStorage（刷新不丢态）
 *  - login/register/logout 写入与清空
 *  - 非 React 上下文（axios 拦截器、socket）通过 useAuthStore.getState() 读取
 */
import { create } from 'zustand';
import type { PublicUser } from '../types/models';

const TOKEN_KEY = 'zhihui_token';
const USER_KEY = 'zhihui_user';

interface AuthState {
  token: string | null;
  user: PublicUser | null;
  setAuth: (token: string, user: PublicUser) => void;
  setUser: (user: PublicUser) => void;
  logout: () => void;
  isFounder: () => boolean;
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}
function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function loadUser(): PublicUser | null {
  const raw = safeGet(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PublicUser;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: safeGet(TOKEN_KEY),
  user: loadUser(),
  setAuth: (token, user) => {
    safeSet(TOKEN_KEY, token);
    safeSet(USER_KEY, JSON.stringify(user));
    set({ token, user });
  },
  setUser: (user) => {
    safeSet(USER_KEY, JSON.stringify(user));
    set({ user });
  },
  logout: () => {
    safeRemove(TOKEN_KEY);
    safeRemove(USER_KEY);
    set({ token: null, user: null });
  },
  isFounder: () => get().user?.role === 'founder',
}));

/** 给非 React 模块（socket）取 token */
export function getToken(): string | null {
  return useAuthStore.getState().token;
}
